import { useEffect } from "react";
import { ArrowRight, Brain, Cloud, HeartHandshake, Lightbulb, Timer } from "lucide-react";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { FAMILIES } from "../../shared/model";
import { FAMILY_META } from "../puzzles/catalog";
import { LinkButton } from "../ui/kit";

const STEPS = [
  { icon: Timer, title: "Three puzzles, ten minutes", body: "Each day: one puzzle for each way of thinking: seeing, bending, planning. About four minutes each, at your pace." },
  { icon: Lightbulb, title: "Nudges, never answers", body: "Stuck? Pebble the turtle asks you a question to think about. Peeking at the answer is allowed. We just keep it honest." },
  { icon: Cloud, title: "It follows you", body: "With a free account your minutes, streak and notes sync between your phone, tablet and computer. Or stay a guest; it all works offline." }
];

export function Landing() {
  const { user } = useSession();
  useEffect(() => {
    document.title = "Ponder: ten minutes a day, your own brain";
  }, []);
  return (
    <div className="landing">
      <header className="landing-nav wrap">
        <Link to="/welcome" className="brand">
          <img src="/favicon-64.png" alt="" width="38" height="38" />
          <span className="wordmark">ponder</span>
        </Link>
        <nav>
          <a href="#puzzles" className="nav-link hide-sm">The puzzles</a>
          <a href="#why" className="nav-link hide-sm">Why no AI?</a>
          {user ? (
            <LinkButton to="/today" kind="primary" size="small">Today's puzzles</LinkButton>
          ) : (
            <>
              <Link to="/login" className="nav-link">Sign in</Link>
              <LinkButton to="/today" kind="primary" size="small">Start thinking</LinkButton>
            </>
          )}
        </nav>
      </header>

      <main>
        <section className="hero wrap">
          <div className="hero-copy">
            <p className="kicker">a daily thinking practice</p>
            <h1>
              Ten minutes a day.
              <br />
              <em>Your own brain.</em>
            </h1>
            <p className="lede">
              Ponder is a small daily habit of puzzles that make you think: geometry, insight, strategy. No AI, no ads, no timers shouting at you. Free, and it syncs across your devices.
            </p>
            <div className="hero-cta">
              <button type="button" className="btn btn-primary big" onClick={() => navigate("/today")}>
                {user ? "Today's puzzles" : "Try today's puzzles"} <ArrowRight size={19} />
              </button>
              {!user && <LinkButton to="/signup" size="big">Create a free account</LinkButton>}
            </div>
            <p className="hand hand-note">no sign-up needed to start ↑</p>
          </div>
          <div className="hero-art">
            <img src="/art/mascot-hero.webp" alt="Pebble the turtle sits on a stone by a sunrise pond, thinking" width="1400" height="933" fetchPriority="high" />
          </div>
        </section>

        <section className="wrap steps" id="how" aria-labelledby="how-h">
          <h2 id="how-h" className="sr-only">How it works</h2>
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="step card">
                <span className="step-icon"><Icon size={22} /></span>
                <h3>{s.title}</h3>
                <p className="muted">{s.body}</p>
              </div>
            );
          })}
        </section>

        <section className="wrap" id="puzzles">
          <div className="section-head">
            <h2>Three ways to think</h2>
            <span className="muted small">and a fresh puzzle in each, every day</span>
          </div>
          <div className="card-grid three">
            {FAMILIES.map((f) => {
              const m = FAMILY_META[f];
              return (
                <Link key={f} to={`/p/${f}-r1`} className="family-card card">
                  <img src={m.cover} alt="" loading="lazy" width="480" height="320" />
                  <div className="fc-body">
                    <span className={"chip " + m.muscle.toLowerCase()}>{m.muscle}</span>
                    <h3>{m.name}</h3>
                    <p className="muted">{m.pitch}</p>
                    <p className="small fc-skill">{m.skill}</p>
                    <span className="fc-try">Try the first one <ArrowRight size={15} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="wrap why" id="why">
          <div className="why-card card">
            <div className="why-icon"><Brain size={28} /></div>
            <div>
              <h2>Why no AI?</h2>
              <p>
                AI is a wonderful tool. But thinking is a muscle, and the only way to grow it is to use it: the effortful, slightly frustrating part where you hold a problem in your head and turn it over.
              </p>
              <p>
                Ponder has no AI in it. The puzzles are checked by plain, honest rules, and the hints are questions to ask yourself. Ten minutes a day is enough to notice the difference.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap final">
          <img src="/art/celebrate.webp" alt="" width="220" height="220" loading="lazy" />
          <div>
            <h2>Ready when you are.</h2>
            <p className="muted">The first puzzle takes about a minute.</p>
            <div className="hero-cta">
              <LinkButton to="/today" kind="primary" size="big">Start thinking <ArrowRight size={19} /></LinkButton>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-foot wrap">
        <span><HeartHandshake size={16} style={{ verticalAlign: "-3px" }} /> Made with care by <a href="https://sara-aminpour.com" target="_blank" rel="noreferrer">Sara Aminpour</a>. Free. No ads. No tracking.</span>
        <span className="muted small">Part of a small collection of things she builds between experiments.</span>
      </footer>
    </div>
  );
}
